import { parser } from '@copera/tiptap/markdown';
import { AIInputStateControl } from '@modules/ai-conversation/components/AIInput';
import { differenceInSeconds } from 'date-fns';
import { useMemo } from 'react';
import { AiChatMessage } from 'src/@types/ai';
import { useGetContentByParts } from './useGetContentByParts';

export const useAISearchThinkResponse = ({
  message,
  store,
}: { message: AiChatMessage; store: AIInputStateControl }) => {
  const { think, doSearch, generateImage } = store();

  const { getContentByAnnotations } = useGetContentByParts();

  const aiResponseContent = useMemo(
    () =>
      message.status === 'loading' && think
        ? message
        : {
            ...message,
            think: message.think || think,
            doSearch: message.doSearch || doSearch,
            generateImage: message.generateImage || generateImage,
          },
    [message, think, doSearch, generateImage],
  );

  const thinkingTime = useMemo(
    () =>
      aiResponseContent.reasoningTime?.start &&
      aiResponseContent.reasoningTime?.end
        ? differenceInSeconds(
            new Date(aiResponseContent.reasoningTime?.end),
            new Date(aiResponseContent.reasoningTime.start),
          ) + 's'
        : '',
    [
      aiResponseContent.reasoningTime?.start,
      aiResponseContent.reasoningTime?.end,
    ],
  );

  const parsedContent = useMemo(
    () =>
      aiResponseContent?.content
        ? parser.parse(aiResponseContent.content).toJSON()
        : '',
    [aiResponseContent.content],
  );

  const { generatedFiles, searchSources } = useMemo(() => {
    return getContentByAnnotations(aiResponseContent.annotations);
  }, [aiResponseContent.annotations, getContentByAnnotations]);

  return {
    aiResponseContent: {
      ...aiResponseContent,
      thinkingTime,
      parsedContent,
      generatedFiles: aiResponseContent.generatedFiles || generatedFiles,
      searchSources: aiResponseContent.searchSources || searchSources,
    },
  };
};
